import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, SITE } from "@/data/content";
import { CoveButton } from "@/components/CoveButton";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cove-cream/90 backdrop-blur-xl border-b border-cove-slate/10 shadow-[0_8px_30px_rgba(41,54,80,0.05)]" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      {!scrolled && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-cove-cream/80 to-transparent" aria-hidden="true" />
      )}

      <nav className="relative mx-auto max-w-[1400px] px-6 md:px-12 h-20 md:h-24 flex items-center justify-between" aria-label="Primary">
        {/* Left: primary links (no logo per brand direction) */}
        <ul className="hidden lg:flex items-center gap-5 xl:gap-7">
          {NAV.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
                className={`relative text-[15px] font-medium tracking-wide transition-colors duration-300 hover:text-cove-rose ${
                  pathname === item.to ? "text-cove-slate" : "text-cove-slate/80"
                }`}
              >
                {item.label}
                {pathname === item.to && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute -bottom-1.5 left-0 h-[2px] w-full bg-cove-rose"
                    transition={{ duration: 0 }}
                  />
                )}
              </Link>
            </li>
          ))}
        </ul>

        {/* Mobile spacer keeps toggle on the right */}
        <span className="lg:hidden" aria-hidden="true" />

        {/* Right: booking CTA */}
        <div className="hidden lg:block">
          <CoveButton calendly variant="primary" testId="nav-book-button" className="px-6 py-3">
            Book a Session
          </CoveButton>
        </div>

        <button
          className="lg:hidden text-cove-slate p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-cove-cream z-40 grain overflow-y-auto overscroll-contain"
            data-testid="mobile-menu"
          >
            <div className="px-6 pt-4 pb-8 flex flex-col min-h-full">
              <ul className="flex flex-col">
                {NAV.map((item, i) => (
                  <motion.li
                    key={item.to}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="border-b border-cove-slate/10"
                  >
                    <Link
                      to={item.to}
                      className="block py-2.5 font-serif text-[1.35rem] text-cove-slate"
                      data-testid={`mobile-nav-link-${item.to.replace(/\//g, "") || "home"}`}
                    >
                      {item.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-5 pb-4">
                <p className="text-sm text-cove-taupe">{SITE.email}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
